import React from 'react';
import { motion } from 'framer-motion';
import { AlertCircle, CheckCircle, Code, FileText, Zap, Sparkles } from 'lucide-react';
import { NetworkIcon } from '../icons/professional';
import { ChatMessage } from '../../stores/useChatStore.js';
import { MessageCard } from './MessageCard.js';
import LogCard from './LogCard.js';
import { ChatActions, parseActionsFromContent } from '../../studio/panels';
import { RunLogStatement } from './RunLogStatement.js';
import { RunTranscriptMessage, type RunTranscriptData } from './RunTranscriptMessage.js';
import { IntelligentRunTranscript, type IntelligentRunData } from './IntelligentRunTranscript.js';
import AssistantResponseCard from './AssistantResponseCard.js';
import { MetaBuildResultCard } from './MetaBuildResultCard.js';
import PlanPreviewCard from './PlanPreviewCard.js';
import ExecutionCard from './ExecutionCard.js';
import IntentBanner from './IntentBanner.js';
import type {
    AgenticContext,
    MetaBuildResult,
    PlanPreviewData,
    StudioRepoContext,
} from '../../stores/useChatStore.js';
import { sanitizeForChat } from '../../utils/textSanitizer.js';

interface MessageRendererProps {
    message: ChatMessage;
    index?: number;
}

const MessageRenderer: React.FC<MessageRendererProps> = ({ message, index = 0 }) => {
    const getSenderIcon = () => {
        // Use SOMA icon for assistant-log and soma-response messages
        if (message.type === 'assistant-log' || message.type === 'soma-response') {
            return (
                <img src="/assets/soma-icon.png" alt="SOMA" className="w-5 h-5 object-contain" />
            );
        }

        switch (message.sender) {
            case 'user':
                return (
                    <img
                        src="/assets/user-avatar.png"
                        alt="User"
                        className="w-5 h-5 rounded-sm object-cover"
                    />
                );
            case 'bee':
                return <NetworkIcon className="w-4 h-4 text-violet-400" />;
            case 'system':
                return <AlertCircle className="w-4 h-4 text-slate-400" />;
            default:
                return <NetworkIcon className="w-4 h-4 text-violet-400" />;
        }
    };

    const getSenderColors = () => {
        switch (message.sender) {
            case 'user':
                return {
                    iconBg: 'bg-violet-600/20',
                    messageBg: 'bg-violet-600/10 text-violet-100',
                    border: 'border-violet-500/20',
                };
            case 'bee':
                return {
                    iconBg: 'bg-violet-600/20',
                    messageBg: 'bg-violet-600/10 text-violet-100',
                    border: 'border-violet-500/20',
                };
            case 'system':
                return {
                    iconBg: 'bg-slate-700/50',
                    messageBg: 'bg-slate-700/30 text-slate-300',
                    border: 'border-slate-600/20',
                };
            default:
                return {
                    iconBg: 'bg-slate-700/50',
                    messageBg: 'bg-slate-700/30 text-slate-300',
                    border: 'border-slate-600/20',
                };
        }
    };

    const getTypeIcon = () => {
        switch (message.type) {
            case 'code':
                return <Code className="w-3 h-3" />;
            case 'pluginCard':
                return <Zap className="w-3 h-3" />;
            case 'taskResult':
                return <CheckCircle className="w-3 h-3" />;
            case 'intelligent-response':
                return <Sparkles className="w-3 h-3 text-blue-400" />;
            default:
                return <FileText className="w-3 h-3" />;
        }
    };

    const colors = getSenderColors();
    const isUser = message.sender === 'user';
    const executionEnvelope = message.metadata?.executionEnvelope || undefined;
    const traceId = message.metadata?.traceId as string | undefined;
    const risk = message.metadata?.risk as string | undefined;
    const showIntentBanner =
        !isUser &&
        executionEnvelope?.mode === 'execution_required' &&
        executionEnvelope?.planOnly !== true;

    return (
        <motion.div
            className={`flex items-start space-x-3 ${
                isUser ? 'flex-row-reverse space-x-reverse' : ''
            }`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
        >
            {/* Sender Avatar */}
            <div className="p-2 rounded-xl icon-container flex-shrink-0">{getSenderIcon()}</div>

            {/* Message Content */}
            <div className={`flex-1 min-w-0 ${isUser ? 'text-right' : ''}`}>
                <div className={`inline-block max-w-[85%] min-w-0 ${isUser ? 'ml-auto' : ''}`}>
                    {/* Message Type Indicator */}
                    {message.type !== 'text' && (
                        <div
                            className={`flex items-center space-x-1 text-xs text-slate-400 mb-1 ${
                                isUser ? 'justify-end' : ''
                            }`}
                        >
                            {getTypeIcon()}
                            <span className="capitalize">{message.type}</span>
                        </div>
                    )}

                    {/* Message Content Based on Type */}
                    {showIntentBanner && (
                        <IntentBanner
                            intentClass={executionEnvelope?.intentClass}
                            reasonCode={executionEnvelope?.reasonCode}
                            traceId={traceId}
                            risk={risk}
                        />
                    )}
                    {executionEnvelope && (
                        <div className="mb-2">
                            <ExecutionCard
                                executionEnvelope={executionEnvelope}
                                traceId={traceId}
                                risk={risk}
                            />
                        </div>
                    )}
                    {message.type === 'text' ? (
                        <div className="arc-glass-panel px-4 py-3 shadow-inner text-sm">
                            <p className="text-sm whitespace-pre-wrap text-slate-200">
                                {sanitizeForChat(message.content)}
                            </p>
                        </div>
                    ) : message.type === 'plan-preview' ? (
                        (() => {
                            try {
                                const data = JSON.parse(message.content) as PlanPreviewData;
                                return <PlanPreviewCard messageId={message.id} data={data} />;
                            } catch {
                                return (
                                    <div className="arc-glass-panel px-4 py-3 shadow-inner text-sm">
                                        <p className="text-sm whitespace-pre-wrap text-slate-200">
                                            {sanitizeForChat(message.content)}
                                        </p>
                                    </div>
                                );
                            }
                        })()
                    ) : message.type === 'run-log' ? (
                        /* Run logs render as intelligent transcript with separate answer card */
                        (() => {
                            // Try to parse as IntelligentRunData for premium display
                            try {
                                const transcriptData = JSON.parse(
                                    message.content
                                ) as IntelligentRunData & { streamToken?: string };
                                if (transcriptData.traceId && transcriptData.status) {
                                    const streamToken =
                                        transcriptData.streamToken ||
                                        (message.metadata?.streamToken as string | undefined);
                                    return (
                                        <div className="space-y-4">
                                            <IntelligentRunTranscript
                                                traceId={transcriptData.traceId}
                                                initialData={transcriptData}
                                                streamToken={streamToken}
                                                headerMeta={{
                                                    intentClass: message.metadata?.intentClass as
                                                        | string
                                                        | undefined,
                                                    intentName: message.metadata?.intentName as
                                                        | string
                                                        | undefined,
                                                    runMode: message.metadata?.runMode as
                                                        | boolean
                                                        | undefined,
                                                    context: message.metadata?.context as
                                                        | AgenticContext
                                                        | null
                                                        | undefined,
                                                    studioContext: message.metadata
                                                        ?.studioContext as
                                                        | StudioRepoContext
                                                        | null
                                                        | undefined,
                                                    metaBuild: message.metadata?.metaBuild as
                                                        | MetaBuildResult
                                                        | null
                                                        | undefined,
                                                }}
                                            />
                                            {message.metadata?.metaBuild && (
                                                <MetaBuildResultCard
                                                    data={message.metadata.metaBuild}
                                                />
                                            )}
                                        </div>
                                    );
                                }
                            } catch {
                                // Not JSON, try legacy format
                            }
                            // Fallback: use traceId from metadata
                            const traceId = message.metadata?.traceId || message.content;
                            const streamToken = message.metadata?.streamToken as string | undefined;
                            return (
                                <div className="space-y-4">
                                    <IntelligentRunTranscript
                                        traceId={traceId}
                                        streamToken={streamToken}
                                        headerMeta={{
                                            intentClass: message.metadata?.intentClass as
                                                | string
                                                | undefined,
                                            intentName: message.metadata?.intentName as
                                                | string
                                                | undefined,
                                            runMode: message.metadata?.runMode as
                                                | boolean
                                                | undefined,
                                            context: message.metadata?.context as
                                                | AgenticContext
                                                | null
                                                | undefined,
                                            studioContext: message.metadata?.studioContext as
                                                | StudioRepoContext
                                                | null
                                                | undefined,
                                            metaBuild: message.metadata?.metaBuild as
                                                | MetaBuildResult
                                                | null
                                                | undefined,
                                        }}
                                    />
                                    {message.metadata?.metaBuild && (
                                        <MetaBuildResultCard data={message.metadata.metaBuild} />
                                    )}
                                </div>
                            );
                        })()
                    ) : message.type === 'soma-response' ||
                      message.type === 'intelligent-response' ? (
                        <AssistantResponseCard message={message} showProvenance />
                    ) : message.type === 'assistant-log' ? (
                        <div className="arc-glass-panel px-4 py-3 shadow-inner text-sm">
                            {(() => {
                                try {
                                    const logData = JSON.parse(message.content);
                                    return <LogCard log={logData.log} />;
                                } catch {
                                    // Fallback: render plain text decision logs
                                    return (
                                        <p className="text-xs text-slate-400 whitespace-pre-wrap">
                                            {message.content}
                                        </p>
                                    );
                                }
                            })()}
                        </div>
                    ) : (
                        <>
                            <MessageCard message={message} colors={colors} />
                            {/* B2: Inline Chat Actions for pluginCard messages */}
                            {message.type === 'pluginCard' && (
                                <ChatActions actions={parseActionsFromContent(message.content)} />
                            )}
                        </>
                    )}

                    {/* Message Metadata */}
                    <div
                        className={`flex items-center space-x-2 text-xs text-slate-400 mt-1 ${
                            isUser ? 'justify-end' : ''
                        }`}
                    >
                        <span>{new Date(message.timestamp).toLocaleTimeString()}</span>
                        {message.buildCredits && (
                            <>
                                <span>•</span>
                                <span>{message.buildCredits} BC</span>
                            </>
                        )}
                        {message.relatedTaskId && (
                            <>
                                <span>•</span>
                                <span className="text-violet-400">
                                    Task: {message.relatedTaskId.slice(-8)}
                                </span>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

export default MessageRenderer;
export { MessageRenderer };
